import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';

import type { CommentLocationGenericWithId } from '@confluence/comments-panel-queries/entry-points/CommentLocation.ts';
import type { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';

import type { LocationValue } from './commentsPanelHelper.ts';

export enum ViewValues {
	OPEN = 'OPEN',
	UNREAD = 'UNREAD',
	DELETED_CONTENT = 'DELETED_CONTENT',
	RESOLVED = 'RESOLVED',
	MENTIONS_ME = 'MENTIONS_ME',
}

export enum SortValues {
	PAGE_ORDER = 'PAGE_ORDER',
	MOST_RECENT = 'MOST_RECENT',
}

export enum CommentMode {
	VIEW = 'view',
	ADD = 'add',
}

export enum AddFooterCommentMode {
	FOOTER = 'footer', // The footer comments section
	STICKY_DRAFT = 'sticky_draft', // The sticky draft that persists outside of the footer
	STICKY_FOOTER = 'sticky_footer', // The sticky footer that persists outside of the footer
}

export type ReplyToCommentUser = {
	displayName?: string | null;
	accountId?: string | null;
};

// Type for cached comment summary - stores the final API response content
// This cache persists across panel open/close cycles within the same session
export type CachedSummaryState = {
	// The raw API response content (JSON string)
	content: string;
	// The pre-computed final markdown for instant display
	finalMarkdown: string;
	// The content ID to validate cache is for current page
	contentId: string;
	// The comment hash when the summary was generated
	// Used to detect stale cache when comments changed while panel was closed
	commentHash: string;
};

export type CommentsPanelState = {
	currentView: ViewValues | undefined;
	currentSort: SortValues;
	// NOTE: When there are more types, should use an array or some other data structure
	showInlineComments: boolean;
	showGeneralComments: boolean;
	currentlySelectedThreadKey: string | undefined;
	initialDataLoadedForViewMap: Record<ViewValues, boolean>;
	replyToCommentId: string | undefined;
	replyToCommentUser: ReplyToCommentUser | undefined;
	// Keep track of the comment IDs that are being edited in the footer
	// In the future, consider either limiting this to one at a time (same as replies) and / or sharing it with the panel
	footerEditCommentIds: Set<string>;
	// Keep track of the which threads the user is currently viewing (used for keyboard shortcuts)
	currentlyRenderedThreads: CommentLocationGenericWithId[];
	location?: LocationValue; // keep track of the location when comments gets rendered
	commentType?: CommentType;
	// Comment mode state for the footer
	commentMode: CommentMode;
	isCommentAddPromptFocused: boolean;
	commentContainerToFocus: string | undefined;
	// ADF content for footer comment draft
	addFooterCommentAdf: DocNode | undefined;
	// Cached comment summary state - persists across panel open/close cycles
	cachedSummary: CachedSummaryState | null;
};

const getInitialState = (): CommentsPanelState => ({
	currentView: undefined,
	currentSort: SortValues.PAGE_ORDER,
	showInlineComments: true,
	showGeneralComments: true,
	currentlySelectedThreadKey: undefined,
	initialDataLoadedForViewMap: {
		[ViewValues.UNREAD]: false,
		[ViewValues.OPEN]: false,
		[ViewValues.DELETED_CONTENT]: false,
		[ViewValues.RESOLVED]: false,
		[ViewValues.MENTIONS_ME]: false,
	},
	replyToCommentId: undefined,
	replyToCommentUser: undefined,
	footerEditCommentIds: new Set(),
	currentlyRenderedThreads: [],
	location: undefined,
	commentType: undefined,
	commentMode: CommentMode.VIEW,
	isCommentAddPromptFocused: false,
	commentContainerToFocus: undefined,
	addFooterCommentAdf: undefined,
	cachedSummary: null,
});

// Actions for managing the comments state in the comments panel
const actions = {
	getCommentsPanelState:
		() =>
		({ getState }: StoreActionApi<CommentsPanelState>) => {
			return getState();
		},
	setCurrentView:
		(view: ViewValues | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentView: view });
		},
	setCurrentSort:
		(sort: SortValues) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentSort: sort });
		},
	setShowInlineComments:
		(showInline?: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ showInlineComments: showInline });
		},
	setShowGeneralComments:
		(showGeneral?: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ showGeneralComments: showGeneral });
		},
	setCurrentlySelectedThreadKey:
		(threadKey: string | undefined, commentType?: CommentType) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentlySelectedThreadKey: threadKey, commentType });
		},
	setInitialDataLoadedForView:
		({ viewToSetLoaded }: { viewToSetLoaded: ViewValues }) =>
		({ getState, setState }: StoreActionApi<CommentsPanelState>) => {
			const { initialDataLoadedForViewMap } = getState();

			initialDataLoadedForViewMap[viewToSetLoaded] = true;

			setState({ initialDataLoadedForViewMap });
		},
	setReplyToCommentId:
		({
			parentCommentId,
			location,
			user,
		}: { parentCommentId?: string; location?: LocationValue; user?: ReplyToCommentUser } = {}) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ replyToCommentId: parentCommentId, location, replyToCommentUser: user });
		},
	resetInitialDataLoadedMap:
		() =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState(getInitialState());
		},
	setCurrentlyRenderedThreads:
		(threads: CommentLocationGenericWithId[]) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentlyRenderedThreads: threads });
		},
	addFooterEditCommentId:
		(commentId: string) =>
		({ setState, getState }: StoreActionApi<CommentsPanelState>) => {
			const { footerEditCommentIds } = getState();
			// Required to change the state object reference to trigger a re-render
			const newFooterEditCommentIds = new Set(footerEditCommentIds);
			newFooterEditCommentIds.add(commentId);
			setState({
				footerEditCommentIds: newFooterEditCommentIds,
			});
		},
	removeFooterEditCommentId:
		(commentId: string) =>
		({ setState, getState }: StoreActionApi<CommentsPanelState>) => {
			const { footerEditCommentIds } = getState();
			// Required to change the state object reference to trigger a re-render
			const newFooterEditCommentIds = new Set(footerEditCommentIds);
			newFooterEditCommentIds.delete(commentId);
			setState({
				footerEditCommentIds: newFooterEditCommentIds,
			});
		},
	setCommentMode:
		(mode: CommentMode) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ commentMode: mode });
		},
	setIsCommentAddPromptFocused:
		(isFocused: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ isCommentAddPromptFocused: isFocused });
		},
	resetCommentMode:
		() =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ commentMode: CommentMode.VIEW, isCommentAddPromptFocused: false });
		},
	setCommentContainerToFocus:
		(commentContainerToFocus: string | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ commentContainerToFocus });
		},
	setAddFooterCommentAdf:
		(addFooterCommentAdf: DocNode | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ addFooterCommentAdf });
		},
	setCachedSummary:
		(cachedSummary: CachedSummaryState | null) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ cachedSummary });
		},
};

const CommentsStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'CommentsStore',
});

export const useCommentsPanel = createHook(CommentsStore);
export const useCommentsPanelActions = createActionsHook(CommentsStore);
export const useCommentsPanelState = createStateHook(CommentsStore);
